import React, { useState } from 'react';
import './PeeList.css';
import ConfirmModal from './ConfirmModal';
import EntryIcon from './EntryIcon';
import { normalizeType, TYPE_LABELS } from '../entryTypes';

const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const dayLabel = (dateString) => {
    const date = new Date(dateString);
    const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'});
};

const countByType = (entries) => {
    const counts = {pee: 0, poo: 0};
    for (const entry of entries) {
        counts[normalizeType(entry)] += 1;
    }
    return counts;
};

const groupByDay = (peeData) => {
    const groups = {};
    for (const pee of peeData) {
        const date = new Date(pee.time);
        const key = startOfDay(date).getTime();
        if (!groups[key]) {
            groups[key] = {key, label: dayLabel(pee.time), entries: []};
        }
        groups[key].entries.push(pee);
    }
    return Object.values(groups).sort((a, b) => b.key - a.key);
};

function PeeList({ peeData, onDelete }) {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setModalIsOpen(true);
    };

    const confirmDelete = async (id) => {
        await onDelete(id);
        setModalIsOpen(false);
    };

    const sortedPeeData = [...peeData].sort((a, b) => new Date(b.time) - new Date(a.time));
    const dayGroups = groupByDay(sortedPeeData);

    const selectedEntry = peeData.find((entry) => entry._id === selectedId);
    const deleteMessage = selectedEntry
        ? `Delete this ${normalizeType(selectedEntry)}?`
        : 'Delete this entry?';

    return (
        <div className="peeList">
            {dayGroups.map((group) => {
                const counts = countByType(group.entries);
                return (
                    <div key={group.key} className="pee-date-group">
                        <h3 className="date-header">
                            {group.label}
                            <span className="date-count">
                                {counts.pee > 0 && (
                                    <span className="date-count-part date-count-pee">
                                        {counts.pee}×
                                        <EntryIcon type="pee" className="date-count-icon"/>
                                    </span>
                                )}
                                {counts.poo > 0 && (
                                    <span className="date-count-part date-count-poo">
                                        {counts.poo}×
                                        <EntryIcon type="poo" className="date-count-icon"/>
                                    </span>
                                )}
                            </span>
                        </h3>
                        <ul>
                            {group.entries.map((pee) => {
                                const entryType = normalizeType(pee);
                                return (
                                    <li key={pee._id}>
                                        <span className="pee-time">
                                            <EntryIcon
                                                type={entryType}
                                                className={`entry-icon entry-icon-${entryType}`}
                                            />
                                            {/* The icon is aria-hidden, so name the type in words */}
                                            <span
                                                className="visually-hidden"
                                                data-testid={`entry-type-${pee._id}`}
                                            >
                                                {TYPE_LABELS[entryType]}
                                            </span>
                                            {formatTime(pee.time)}
                                            {pee.pending && <span className="pending-chip">pending</span>}
                                        </span>
                                        <button
                                            className="delete-button"
                                            aria-label="Delete entry"
                                            onClick={() => handleDeleteClick(pee._id)}
                                        >
                                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                                <path
                                                    d="M9 3h6l1 2h4v2H4V5h4l1-2zm-3 6h12l-.9 11.1a2 2 0 0 1-2 1.9H8.9a2 2 0 0 1-2-1.9L6 9zm4 2v8h1.5v-8H10zm3 0v8h1.5v-8H13z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            })}
            <ConfirmModal
                isOpen={modalIsOpen}
                onRequestClose={() => setModalIsOpen(false)}
                onConfirm={() => confirmDelete(selectedId)}
                message={deleteMessage}
            />
        </div>
    );
}

export default PeeList;
